/**
 * apiFetch — centralized fetch wrapper for all authenticated API calls.
 *
 * Security model:
 * - Automatically attaches `Authorization: Bearer <token>` from the registered
 *   token getter (set by AuthProvider on every auth state change).
 * - On 401: attempts a silent token refresh (POST /api/auth/refresh). If the
 *   refresh succeeds the original request is retried once. If it fails, the
 *   registered logout handler is called and a "Session expired" toast is shown.
 * - On 403: shows an "Access denied" toast (request is NOT retried).
 * - On 5xx / network error: shows a "Server error" toast.
 *
 * Usage:
 *   All functions in `lib/*.ts` call `apiFetch(url, options)` instead of
 *   `fetch(url, options)`. No manual token plumbing needed at the call site.
 *
 * Registration (done once in AuthProvider):
 *   import { registerAuthHandlers } from './apiFetch';
 *   registerAuthHandlers({ getToken, getRefreshToken, onRefreshSuccess, onAuthFailure });
 */

import { toast } from 'sonner';
import { API_BASE_URL } from './config';

// ---------------------------------------------------------------------------
// Auth handler registry — set by AuthProvider at runtime
// ---------------------------------------------------------------------------

type AuthHandlers = {
  /** Returns the current access token (or null if not authenticated). */
  getToken: () => string | null;
  /** Returns the current refresh token (or null). */
  getRefreshToken: () => string | null;
  /** Called when a refresh succeeds — stores the new access token. */
  onRefreshSuccess: (newAccessToken: string, newRefreshToken: string) => void;
  /** Called when refresh fails or no tokens exist — triggers logout + redirect. */
  onAuthFailure: () => void;
};

let _handlers: AuthHandlers | null = null;

export function registerAuthHandlers(handlers: AuthHandlers): void {
  _handlers = handlers;
}

/** Returns the current access token — used to build stream URLs for window.open() calls. */
export function getAuthToken(): string | null {
  return _handlers?.getToken() ?? null;
}

// ---------------------------------------------------------------------------
// Refresh state — prevent concurrent refresh attempts
// ---------------------------------------------------------------------------

let _refreshPromise: Promise<boolean> | null = null;

async function attemptRefresh(): Promise<boolean> {
  if (_refreshPromise) return _refreshPromise;

  _refreshPromise = (async () => {
    const refreshTok = _handlers?.getRefreshToken();
    if (!refreshTok) return false;

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: refreshTok }),
      });
      const data = await res.json();
      if (data.success && data.data?.accessToken) {
        _handlers?.onRefreshSuccess(data.data.accessToken, data.data.refreshToken ?? refreshTok);
        return true;
      }
    } catch {
      // network error during refresh
    }
    return false;
  })().finally(() => {
    _refreshPromise = null;
  });

  return _refreshPromise;
}

// ---------------------------------------------------------------------------
// Core apiFetch
// ---------------------------------------------------------------------------

type ApiFetchOptions = RequestInit & {
  /** Skip attaching the Authorization header (e.g. for public endpoints). */
  skipAuth?: boolean;
  /** Internal flag — marks this as a retry after token refresh. */
  _isRetry?: boolean;
};

export async function apiFetch(url: string, options: ApiFetchOptions = {}): Promise<Response> {
  const { skipAuth = false, _isRetry = false, ...fetchOptions } = options;

  // Attach auth header
  if (!skipAuth && _handlers) {
    const token = _handlers.getToken();
    if (token) {
      fetchOptions.headers = {
        ...fetchOptions.headers,
        Authorization: `Bearer ${token}`,
      };
    }
  }

  let response: Response;
  try {
    response = await fetch(url, fetchOptions);
  } catch {
    toast.error('Network error. Check your connection and try again.');
    throw new Error('Network error');
  }

  // 401 — try silent refresh then retry once
  if (response.status === 401 && !_isRetry) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      // Retry the original request with the new token
      const newToken = _handlers?.getToken();
      return apiFetch(url, {
        ...options,
        _isRetry: true,
        headers: {
          ...options.headers,
          ...(newToken ? { Authorization: `Bearer ${newToken}` } : {}),
        },
      });
    }
    // Refresh failed — session is dead
    toast.error('Your session has expired. Please log in again.');
    _handlers?.onAuthFailure();
    throw new Error('Session expired');
  }

  // 403 — access denied
  if (response.status === 403) {
    toast.error('Access denied. You don\'t have permission to perform this action.');
    // Don't throw — let the caller handle the response body
  }

  // 5xx server errors
  if (response.status >= 500) {
    toast.error('Server error. Please try again in a moment.');
  }

  return response;
}

// ---------------------------------------------------------------------------
// Convenience helpers that mirror fetch() signatures used by lib/*.ts
// ---------------------------------------------------------------------------

export function apiFetchJSON<T = unknown>(url: string, options?: ApiFetchOptions): Promise<T> {
  return apiFetch(url, options).then((r) => r.json() as Promise<T>);
}
